<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { Label } from '@/Components/ui/label'
import axios from 'axios'
import teacher from '@/routes/teacher'
import '@mux/mux-player'

const props = defineProps({
  modelValue: String, // mux_upload_id
  playbackId: String, // existing mux_playback_id for viewing
  title: String, // video title for alt text
  editable: {
    type: Boolean,
    default: true
  },
  showError: Boolean,
  allowReplacement: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'update:playbackId', 'upload:start', 'upload:complete', 'upload:error', 'status:ready'])

// Parse size string (e.g. "1000MB", "1GB") to bytes
const parseSizeToBytes = (size) => {
  const match = size.match(/^(\d+)\s*(MB|GB)$/i)
  if (!match) return 1000 * 1024 * 1024 // fallback 1000MB
  const value = parseInt(match[1])
  const unit = match[2].toUpperCase()
  return unit === 'GB' ? value * 1024 * 1024 * 1024 : value * 1024 * 1024
}

const page = usePage()
const maxSizeConfig = computed(() => page.props.config?.mux?.content_video_max_size || '1000MB')
const maxSizeBytes = computed(() => parseSizeToBytes(maxSizeConfig.value))

// Upload state
const isReplacingVideo = ref(false)
const isUploading = ref(false)
const isUploaded = ref(false)
const uploadProgress = ref(0)
const fileInputRef = ref(null)

// Processing state
const isCheckingStatus = ref(false)
const processingStatus = ref(null) // 'checking', 'processing', 'ready', 'error'
const thumbnailUrl = ref(null)
const localPlaybackId = ref(null)
const statusCheckInterval = ref(null)
const statusCheckCount = ref(0)
const maxStatusChecks = 30 // Max 30 checks (about 2.5 minutes with 5s intervals)

// Watch for playbackId changes to reset replacement state
watch(() => props.playbackId, (newVal) => {
  isReplacingVideo.value = false
  isUploaded.value = false
  if (newVal) {
    localPlaybackId.value = newVal
    processingStatus.value = 'ready'
    thumbnailUrl.value = `https://image.mux.com/${newVal}/thumbnail.jpg?time=0`
    stopStatusPolling()
  }
})

// Status polling functions
const startStatusPolling = (uploadId) => {
  stopStatusPolling() // Clear any existing interval
  statusCheckCount.value = 0
  processingStatus.value = 'checking'

  // Initial check after 5 seconds
  setTimeout(() => {
    checkUploadStatus(uploadId)
  }, 5000)

  // Then check every 5 seconds
  statusCheckInterval.value = setInterval(() => {
    checkUploadStatus(uploadId)
  }, 5000)
}

const stopStatusPolling = () => {
  if (statusCheckInterval.value) {
    clearInterval(statusCheckInterval.value)
    statusCheckInterval.value = null
  }
}

const checkUploadStatus = async (uploadId) => {
  if (!uploadId) return

  statusCheckCount.value++
  isCheckingStatus.value = true

  try {
    const response = await axios.post(teacher.content.muxUploadStatus().url, {
      upload_id: uploadId
    })

    const data = response.data

    if (data.ready && data.playback_id) {
      // Video is ready
      processingStatus.value = 'ready'
      localPlaybackId.value = data.playback_id
      thumbnailUrl.value = data.thumbnail_url
      stopStatusPolling()
      emit('update:playbackId', data.playback_id)
      emit('status:ready', {
        playback_id: data.playback_id,
        thumbnail_url: data.thumbnail_url,
        duration: data.duration
      })
    } else if (data.upload_status === 'errored' || data.asset_status === 'errored') {
      // Error occurred
      processingStatus.value = 'error'
      stopStatusPolling()
      emit('upload:error', new Error('Video processing failed'))
    } else {
      // Still processing
      processingStatus.value = 'processing'

      // Stop polling after max checks
      if (statusCheckCount.value >= maxStatusChecks) {
        stopStatusPolling()
        processingStatus.value = 'timeout'
      }
    }
  } catch (error) {
    console.error('Status check failed:', error)
    // Don't stop polling on network errors, just log
  } finally {
    isCheckingStatus.value = false
  }
}

const uploadError = ref(null)

const handleFileSelect = async (e) => {
  const file = e.target.files[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('video/')) {
    uploadError.value = 'Please select a video file'
    return
  }

  // Validate file size
  if (file.size > maxSizeBytes.value) {
    uploadError.value = `Video must be smaller than ${maxSizeConfig.value}`
    return
  }

  uploadError.value = null
  isUploading.value = true
  uploadProgress.value = 0
  processingStatus.value = null
  thumbnailUrl.value = null
  localPlaybackId.value = null
  emit('upload:start')

  try {
    // Get Upload URL
    const response = await axios.post(teacher.content.muxUploadUrl().url)

    if (!response.data.upload_url) throw new Error('Failed to get upload URL')

    const uploadUrl = response.data.upload_url
    const uploadId = response.data.upload_id

    emit('update:modelValue', uploadId)

    // Start UpChunk Upload
    const upload = window.UpChunk.createUpload({
      endpoint: uploadUrl,
      file: file,
      chunkSize: 5120, // 5MB chunks
    })

    upload.on('progress', (progress) => {
      uploadProgress.value = progress.detail
    })

    upload.on('success', () => {
      isUploading.value = false
      isUploaded.value = true
      emit('upload:complete', uploadId)
      // Start polling for status
      startStatusPolling(uploadId)
    })

    upload.on('error', (err) => {
      console.error(err)
      isUploading.value = false
      emit('upload:error', err)
      alert('Upload failed. Please try again.')
    })

  } catch (error) {
    console.error(error)
    isUploading.value = false
    emit('upload:error', error)
    alert('Error starting upload.')
  }
}

const resetUpload = () => {
  isUploaded.value = false
  isReplacingVideo.value = false
  processingStatus.value = null
  thumbnailUrl.value = null
  localPlaybackId.value = null
  stopStatusPolling()
  emit('update:modelValue', '')
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

// Load UpChunk library
onMounted(() => {
  if (!window.UpChunk) {
    const script = document.createElement('script')
    script.src = 'https://unpkg.com/@mux/upchunk@3'
    script.async = true
    document.head.appendChild(script)
  }

  // Initialize local state from props
  if (props.playbackId) {
    localPlaybackId.value = props.playbackId
    thumbnailUrl.value = `https://image.mux.com/${props.playbackId}/thumbnail.jpg?time=0`
    processingStatus.value = 'ready'
  } else if (props.modelValue && !props.playbackId) {
    // Have upload ID but no playback ID - start polling
    isUploaded.value = true
    startStatusPolling(props.modelValue)
  }
})

onUnmounted(() => {
  stopStatusPolling()
})
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Upload Error Message -->
    <div v-if="uploadError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-start gap-2">
        <span class="material-symbols-outlined text-red-600 text-sm mt-0.5">error</span>
        <p class="text-sm text-red-700">{{ uploadError }}</p>
      </div>
    </div>

    <!-- Header with Replace Button (only for edit mode) -->
    <div v-if="editable && allowReplacement" class="flex items-center justify-between">
      <Label class="text-gray-900 text-sm font-semibold">Video</Label>
      <Button
        type="button"
        variant="outline"
        size="sm"
        @click="isReplacingVideo = !isReplacingVideo"
        v-if="!isUploading && !isUploaded && playbackId"
      >
        {{ isReplacingVideo ? 'Cancel Replace' : 'Replace Video' }}
      </Button>
    </div>

    <!-- Existing Video Player (when not replacing) -->
    <div
      v-if="playbackId && !isReplacingVideo && !isUploading && !isUploaded"
      class="relative w-full aspect-video rounded-xl overflow-hidden bg-black shadow-lg"
    >
      <mux-player
        stream-type="on-demand"
        :playback-id="playbackId"
        :metadata-video-title="title || 'Video'"
        primary-color="#2563EB"
        class="w-full h-full"
      ></mux-player>
    </div>

    <!-- Upload Dropzone (when editable and no video, or replacing) -->
    <div
      v-if="editable && ((isReplacingVideo || !playbackId) && !isUploading && !isUploaded)"
      @click="triggerFileInput"
      :class="{'border-red-500 bg-red-50': showError, 'border-gray-300 bg-gray-50 hover:bg-blue-50/50 hover:border-blue-600/50': !showError}"
      class="group relative flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-xl transition-all cursor-pointer"
    >
      <div class="flex flex-col items-center justify-center pt-5 pb-6 text-center px-4">
        <div class="mb-4 rounded-full bg-white p-4 shadow-sm group-hover:scale-110 transition-transform duration-300 text-blue-600">
          <span class="material-symbols-outlined text-3xl">cloud_upload</span>
        </div>
        <p class="mb-2 text-lg font-medium text-gray-900">
          <span class="text-blue-600 hover:underline font-bold">Click to upload</span> or drag and drop
        </p>
        <p class="text-sm text-gray-500">MP4, MOV (MAX. {{ maxSizeConfig }})</p>
      </div>
      <input
        ref="fileInputRef"
        @change="handleFileSelect"
        class="hidden"
        type="file"
        accept="video/*"
      />
    </div>

    <!-- Uploading State -->
    <div
      v-if="isUploading"
      class="w-full h-64 border-2 border-gray-200 rounded-xl bg-gray-50 flex flex-col items-center justify-center p-8"
    >
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Uploading Video...</h3>
      <div class="w-full max-w-md bg-gray-200 rounded-full h-2.5 mb-2">
        <div
          class="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
          :style="`width: ${uploadProgress}%`"
        ></div>
      </div>
      <p class="text-sm text-gray-600">{{ Math.round(uploadProgress) }}%</p>
    </div>

    <!-- Uploaded State - Processing/Ready with Thumbnail -->
    <div
      v-if="isUploaded"
      class="relative w-full aspect-video rounded-xl overflow-hidden bg-black shadow-lg"
    >
      <!-- Show thumbnail when available -->
      <template v-if="thumbnailUrl">
        <img
          :src="thumbnailUrl"
          :alt="title || 'Video thumbnail'"
          class="w-full h-full object-cover"
        />
        <!-- Processing overlay -->
        <div
          v-if="processingStatus === 'processing' || processingStatus === 'checking'"
          class="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white"
        >
          <span class="material-symbols-outlined text-3xl mb-3 animate-spin">sync</span>
          <span class="font-medium">Finalizing video...</span>
          <span class="text-sm text-gray-300 mt-1">Almost ready</span>
        </div>
        <!-- Ready indicator -->
        <div
          v-else-if="processingStatus === 'ready'"
          class="absolute bottom-4 left-4 bg-green-500/90 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1"
        >
          <span class="material-symbols-outlined text-base">check_circle</span>
          Ready
        </div>
      </template>

      <!-- Fallback: No thumbnail yet, show loading -->
      <template v-else>
        <div class="w-full h-full flex flex-col items-center justify-center text-white">
          <span class="material-symbols-outlined text-3xl mb-3 animate-spin">sync</span>
          <span class="font-medium">Video uploaded! Processing...</span>
          <span class="text-sm text-gray-400 mt-1">You can save your content now</span>
        </div>
      </template>

      <!-- Error State -->
      <div
        v-if="processingStatus === 'error'"
        class="absolute inset-0 bg-red-900/80 flex flex-col items-center justify-center text-white"
      >
        <span class="material-symbols-outlined text-3xl mb-3">error</span>
        <span class="font-medium">Processing failed</span>
        <span class="text-sm text-gray-300 mt-1">Please try uploading again</span>
      </div>

      <!-- Timeout State -->
      <div
        v-if="processingStatus === 'timeout'"
        class="absolute inset-0 bg-orange-900/80 flex flex-col items-center justify-center text-white"
      >
        <span class="material-symbols-outlined text-3xl mb-3">schedule</span>
        <span class="font-medium">Taking longer than expected</span>
        <span class="text-sm text-gray-300 mt-1">Your video will be ready soon</span>
      </div>

      <!-- Reset Button -->
      <button
        v-if="editable"
        type="button"
        @click="resetUpload"
        class="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full p-2 transition-colors"
      >
        <span class="material-symbols-outlined text-xl">close</span>
      </button>
    </div>

    <!-- Error Message Slot -->
    <slot name="error"></slot>
  </div>
</template>
